import React from 'react'
import { createPortal } from 'react-dom'

interface Props {
  value: string
  onChange: (value: string) => void
  className?: string
  style?: React.CSSProperties
  placeholder?: string
  disabled?: boolean
}

interface Suggestion {
  placeId: string
  mainText: string
  secondaryText: string
  fullText: string
}

let sessionToken: google.maps.places.AutocompleteSessionToken | null = null

function getSessionToken() {
  if (!sessionToken) sessionToken = new google.maps.places.AutocompleteSessionToken()
  return sessionToken
}

function resetSessionToken() {
  sessionToken = null
}

export function LocationInput({ value, onChange, className, style, placeholder = 'City, country…', disabled }: Props) {
  const [query, setQuery] = React.useState(value)
  const [suggestions, setSuggestions] = React.useState<Suggestion[]>([])
  const [open, setOpen] = React.useState(false)
  const [ready, setReady] = React.useState(false)
  const [dropdownPos, setDropdownPos] = React.useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 })

  const inputRef = React.useRef<HTMLInputElement>(null)
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  // Load the Places library (new API)
  React.useEffect(() => {
    async function load() {
      try {
        await google.maps.importLibrary('places')
        setReady(true)
      } catch {
        // Google Maps not loaded yet — will be triggered once LoadScript finishes
      }
    }
    if (typeof google !== 'undefined') {
      void load()
    }
  }, [])

  // Sync when parent resets the value
  React.useEffect(() => { setQuery(value) }, [value])

  // Reposition dropdown on scroll/resize
  React.useEffect(() => {
    if (!open) return
    function reposition() {
      if (!inputRef.current) return
      const rect = inputRef.current.getBoundingClientRect()
      setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width })
    }
    reposition()
    window.addEventListener('scroll', reposition, true)
    window.addEventListener('resize', reposition)
    return () => {
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
    }
  }, [open])

  // Close on outside click
  React.useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      if (inputRef.current && !inputRef.current.contains(e.target as Node)) {
        setSuggestions([])
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [])

  async function fetchSuggestions(input: string) {
    if (!input.trim() || !ready) { setSuggestions([]); return }
    try {
      const { AutocompleteSuggestion } = await google.maps.importLibrary('places') as google.maps.PlacesLibrary
      const { suggestions: results } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        sessionToken: getSessionToken(),
        includedPrimaryTypes: ['locality', 'administrative_area_level_1'],
      })
      setSuggestions(
        results
          .filter(s => s.placePrediction !== null)
          .map(s => {
            const p = s.placePrediction!
            return {
              placeId: p.placeId,
              mainText: (p.mainText as { toString(): string } | null)?.toString() ?? p.text.toString(),
              secondaryText: (p.secondaryText as { toString(): string } | null)?.toString() ?? '',
              fullText: p.text.toString(),
            }
          })
      )
      setOpen(true)
    } catch {
      setSuggestions([])
    }
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const v = e.target.value
    setQuery(v)
    onChange(v)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => void fetchSuggestions(v), 300)
  }

  function handleSelect(s: Suggestion) {
    setQuery(s.fullText)
    onChange(s.fullText)
    setSuggestions([])
    setOpen(false)
    resetSessionToken() // new session after each selection (billing best practice)
  }

  const showDropdown = open && suggestions.length > 0

  return (
    <>
      <input
        ref={inputRef}
        className={className}
        style={style}
        value={query}
        onChange={handleChange}
        onFocus={() => { if (suggestions.length > 0) setOpen(true) }}
        disabled={disabled || !ready}
        placeholder={placeholder}
        autoComplete="off"
        type="text"
      />
      {showDropdown && createPortal(
        <ul
          className="location-dropdown"
          role="listbox"
          style={{ top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}
        >
          {suggestions.map(s => (
            <li
              key={s.placeId}
              className="location-dropdown-item"
              role="option"
              onMouseDown={() => handleSelect(s)}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="10" r="3" />
                <path d="M12 2a8 8 0 0 0-8 8c0 5.33 8 12 8 12s8-6.67 8-12a8 8 0 0 0-8-8z" />
              </svg>
              <span>
                <span className="location-dropdown-main">{s.mainText}</span>
                <span className="location-dropdown-secondary">{s.secondaryText}</span>
              </span>
            </li>
          ))}
        </ul>,
        document.body
      )}
    </>
  )
}
